// src/pages/Login.jsx
//
// Signing in with a WhatsApp one-time code.
//
// This used to be a username and a password, both compared against a single
// pair in the server's environment — so every operator shared one login and
// there was no way to take one person's access away. Now the number itself is
// the identity: it has to be on AUTH_ALLOWED_PHONES, and the code goes to that
// number over WhatsApp.
//
// The two-step shape (and the six boxes, and the paste handling) mirrors the
// Settings unlock screen deliberately — the same gesture in both places, so
// neither has to be learned twice.

import React, { useEffect, useRef, useState } from 'react';
import { FiSmartphone, FiArrowLeft } from 'react-icons/fi';

const OTP_LENGTH = 6;

async function api(path, options = {}) {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options,
  });
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { detail: text }; }
  if (!res.ok) throw new Error(body?.detail || body?.message || `HTTP ${res.status}`);
  return body;
}

export default function Login({ onLoginSuccess }) {
  const [config, setConfig] = useState(null);   // null while loading
  const [phone, setPhone] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Guards the auto-submit: filling the last box fires verify, and without
  // this a re-render mid-request could fire it a second time on the same code.
  const submitting = useRef(false);

  useEffect(() => {
    api('/auth/config')
      .then(setConfig)
      .catch((e) => setConfig({ users_configured: 0, hint: e.message }));
  }, []);

  // Mirrors the server's own resend limit, so the button does not invite a
  // request that can only come back as a 429.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async (e) => {
    e?.preventDefault?.();
    setError('');
    setNotice('');
    if (!phone.replace(/\D/g, '')) {
      setError('Enter your WhatsApp number, with the country code.');
      return;
    }

    setSending(true);
    try {
      const out = await api('/auth/otp/request', {
        method: 'POST',
        body: JSON.stringify({ phone }),
      });
      setChallenge(out);
      setNotice(out.message);
      setCooldown(30);
      setDigits(Array(OTP_LENGTH).fill(''));
      setTimeout(() => document.getElementById('otp-0')?.focus(), 50);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const verify = async (code) => {
    if (submitting.current) return;
    submitting.current = true;
    setVerifying(true);
    setError('');
    try {
      // The tokens come back in the body too, for API clients. The dashboard
      // does not keep them: the server also sets them as httpOnly cookies,
      // which the browser attaches to every /api call by itself and which a
      // script on the page cannot read back out.
      const out = await api('/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ challenge_id: challenge.challenge_id, code }),
      });
      onLoginSuccess?.(out.user);
    } catch (err) {
      setError(err.message);
      setDigits(Array(OTP_LENGTH).fill(''));
      document.getElementById('otp-0')?.focus();
    } finally {
      submitting.current = false;
      setVerifying(false);
    }
  };

  const onDigit = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    // Pasting the whole code into the first box should just work.
    if (val.length > 1) {
      const spread = val.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
      const next = Array(OTP_LENGTH).fill('');
      spread.forEach((d, i) => { next[i] = d; });
      setDigits(next);
      setError('');
      if (spread.length === OTP_LENGTH) verify(next.join(''));
      return;
    }
    const next = [...digits];
    next[idx] = val.slice(-1);
    setDigits(next);
    setError('');
    if (val && idx < OTP_LENGTH - 1) document.getElementById(`otp-${idx + 1}`)?.focus();
    if (next.every((d) => d !== '')) verify(next.join(''));
  };

  const onDigitKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      document.getElementById(`otp-${idx - 1}`)?.focus();
    }
  };

  const startOver = () => {
    setChallenge(null);
    setDigits(Array(OTP_LENGTH).fill(''));
    setError('');
    setNotice('');
    setCooldown(0);
  };

  const nobodyConfigured = config && !config.users_configured;

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex flex-col items-center gap-2 mb-5">
          <img src="/logo.png" alt="Logo" className="w-48 h-20 object-contain mb-2" />
          <h1 className="text-2xl font-bold tracking-tight text-ink">
            MAA PLATFORM
          </h1>
        </div>

        <p className="text-sm text-gray-600 mt-1 text-center">
          {config === null
            ? 'Checking…'
            : nobodyConfigured
              ? 'This server has no one authorised to sign in yet.'
              : challenge
                ? `Enter the ${OTP_LENGTH}-digit code sent on WhatsApp to ${challenge.masked_phone}.`
                : 'Sign in with a one-time code sent to your WhatsApp number.'}
        </p>

        {nobodyConfigured && (
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {config.hint || 'Ask an administrator to set AUTH_ALLOWED_PHONES on this server.'}
          </div>
        )}

        {config?.users_configured > 0 && !challenge && (
          <form onSubmit={sendCode} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1" htmlFor="login-phone">
                WhatsApp number
              </label>
              <input
                id="login-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="919812345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500">
                Include the country code, for example 91 for India.
              </p>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={sending}
              className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 font-semibold text-ink hover:bg-brand-600 disabled:opacity-60"
            >
              <FiSmartphone className="h-5 w-5" />
              {sending ? 'Sending…' : 'Send code on WhatsApp'}
            </button>
          </form>
        )}

        {challenge && (
          <div className="mt-6">
            <div className="mb-4 flex justify-center gap-2">
              {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  type="tel"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={i === 0 ? OTP_LENGTH : 1}
                  value={digits[i]}
                  onChange={(e) => onDigit(i, e.target.value)}
                  onKeyDown={(e) => onDigitKeyDown(i, e)}
                  disabled={verifying}
                  className="h-14 w-11 rounded-lg border-2 border-gray-200 text-center text-2xl outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 sm:h-16 sm:w-12"
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {verifying && (
              <p className="mb-2 text-center text-sm text-gray-500">Signing you in…</p>
            )}

            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              onClick={sendCode}
              disabled={sending || cooldown > 0}
              className="min-h-[44px] w-full rounded-xl border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? 'Sending…' : 'Resend code'}
            </button>

            <button
              onClick={startOver}
              className="mt-3 flex w-full items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <FiArrowLeft className="h-4 w-4" />
              Use a different number
            </button>
          </div>
        )}

        {notice && !error && (
          <p className="mt-4 text-center text-sm text-green-700">{notice}</p>
        )}
      </div>
    </div>
  );
}
